/**
 * Admin — Union Claims verification panel — /admin/union-claims
 * Platform admin reviews pending claims, previews cert, approves,
 * requests more info, or rejects.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ShieldCheck, Clock3, MessageCircleWarning, BadgeCheck, XCircle,
  FileText, Mail, Phone, ExternalLink, ArrowLeft, Loader2,
} from "lucide-react";

const STATUSES = ["pending", "info_requested", "approved", "rejected"];
const STATUS_LABEL = {
  pending: "Pending", info_requested: "Info needed",
  approved: "Approved", rejected: "Rejected",
};
const STATUS_COLOR = {
  pending: "#F5B82E", info_requested: "#F59E0B",
  approved: "#22C55E", rejected: "#EF4444",
};

export default function AdminUnionClaims() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("pending");
  const [rows, setRows]     = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setL]     = useState(true);
  const [action, setAction] = useState(null);   // {claim, kind} kind ∈ approve|request_info|reject
  const [note, setNote]     = useState("");
  const [saving, setSaving] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [previewMime, setPreviewMime] = useState(null);

  async function load() {
    setL(true);
    try {
      const { data } = await api.get(`/admin/union-claims`, { params: { status } });
      setRows(data.claims);
      setCounts(data.counts);
    } catch (err) {
      if (err.response?.status === 403 || err.response?.status === 401) {
        toast.error("Admin access required");
        nav("/");
        return;
      }
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Load failed");
    } finally { setL(false); }
  }
  useEffect(() => { if (!authLoading && user) void load(); /* eslint-disable-next-line */ }, [status, authLoading, user]);

  useEffect(() => () => {
    // cleanup preview object url on unmount
    if (previewSrc?.startsWith("blob:")) URL.revokeObjectURL(previewSrc);
  }, [previewSrc]);

  if (authLoading) return null;
  if (!user) return null;

  async function openPreview(claimId) {
    try {
      const { data, headers } = await api.get(
        `/admin/union-claims/${claimId}/cert`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(data);
      setPreviewSrc(url);
      setPreviewMime(headers["content-type"] || "");
    } catch (err) {
      toast.error("Could not load certificate");
    }
  }

  async function submitAction() {
    if (!action) return;
    setSaving(true);
    try {
      if (action.kind === "approve") {
        await api.post(`/admin/union-claims/${action.claim.id}/approve`,
          { admin_note: note });
        toast.success("Approved — claimant notified");
      } else if (action.kind === "request_info") {
        if (note.trim().length < 3) throw new Error("Please describe what's missing.");
        await api.post(`/admin/union-claims/${action.claim.id}/request-info`,
          { admin_note: note });
        toast.success("Info request sent");
      } else if (action.kind === "reject") {
        if (note.trim().length < 3) throw new Error("Please add a rejection reason.");
        await api.post(`/admin/union-claims/${action.claim.id}/reject`,
          { reason: note });
        toast.success("Rejected — claimant notified");
      }
      setAction(null); setNote("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white" data-testid="admin-union-claims">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/" className="text-[10px] tracking-[0.25em] uppercase text-white/55 hover:text-white inline-flex items-center gap-1.5"
          data-testid="admin-back-home">
          <ArrowLeft size={12} /> Home
        </Link>
        <div className="mt-3 text-[10px] tracking-[0.32em] uppercase text-fuchsia-400">
          Onboarding · Verify Claims
        </div>
        <h1 className="font-display text-3xl uppercase tracking-tight">Union Claims</h1>
        <div className="mt-1 text-sm text-white/55">
          Approve or request additional docs — outbound emails come from{" "}
          <b>onboarding@callsheet.app</b>.
        </div>

        {/* Status tabs */}
        <div className="mt-6 flex flex-wrap gap-2" data-testid="admin-status-tabs">
          {STATUSES.map(s => (
            <button key={s} onClick={() => setStatus(s)}
              className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.24em] border ${status === s ? "border-white/50 text-white bg-white/5" : "border-white/10 text-white/55 hover:border-white/25"}`}
              data-testid={`admin-tab-${s}`}>
              {STATUS_LABEL[s]}
              <span className="ml-2 tabular-nums" style={{ color: STATUS_COLOR[s] }}>
                {counts[s] || 0}
              </span>
            </button>
          ))}
        </div>

        {/* List */}
        {loading && <div className="mt-10 text-center text-white/40 uppercase tracking-[0.3em] text-xs">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="mt-10 text-center py-10 text-white/45" data-testid="admin-empty">
            No claims in this status.
          </div>
        )}
        {!loading && rows.length > 0 && (
          <div className="mt-6 space-y-3" data-testid="admin-claims-list">
            {rows.map(c => (
              <div key={c.id} className="rounded-2xl border border-white/10 p-5"
                style={{ borderLeft: `3px solid ${c.association_color}` }}
                data-testid={`admin-claim-row-${c.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="font-display text-lg uppercase tracking-tight"
                      style={{ color: c.association_color }}>
                      {c.association_name}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 mt-0.5">
                      Claimant: {c.claimant_name} · {c.designation}
                    </div>
                    <div className="mt-2 flex items-center gap-4 text-xs text-white/70 flex-wrap">
                      <span className="inline-flex items-center gap-1"><Mail size={11} /> {c.claimant_email}</span>
                      {c.claimant_phone && <span className="inline-flex items-center gap-1"><Phone size={11} /> {c.claimant_phone}</span>}
                    </div>
                    {c.proposed_contact && Object.keys(c.proposed_contact).length > 0 && (
                      <div className="mt-3 text-[11px] text-white/50">
                        <div className="uppercase tracking-[0.24em] text-white/40 text-[9px] mb-1">
                          Proposed public contact
                        </div>
                        <div className="space-x-3">
                          {c.proposed_contact.website && <span>🌐 {c.proposed_contact.website}</span>}
                          {c.proposed_contact.email   && <span>✉ {c.proposed_contact.email}</span>}
                          {c.proposed_contact.phone   && <span>📞 {c.proposed_contact.phone}</span>}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">
                      {new Date(c.created_at).toLocaleString()}
                    </div>
                    {c.has_cert && (
                      <button onClick={() => openPreview(c.id)}
                        className="text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-full border border-white/15 hover:border-fuchsia-400 hover:text-fuchsia-300 inline-flex items-center gap-1.5"
                        data-testid={`admin-preview-cert-${c.id}`}>
                        <FileText size={11} /> Preview cert
                      </button>
                    )}
                  </div>
                </div>

                {/* Action buttons — only for actionable states */}
                {(c.status === "pending" || c.status === "info_requested") && (
                  <div className="mt-4 flex gap-2 flex-wrap">
                    <Button onClick={() => { setAction({claim: c, kind: "approve"}); setNote(""); }}
                      className="btn-themed rounded-full uppercase tracking-[0.22em] text-xs h-9 px-4"
                      data-testid={`admin-approve-${c.id}`}>
                      <BadgeCheck size={13} className="mr-1.5" /> Approve
                    </Button>
                    <Button variant="outline"
                      onClick={() => { setAction({claim: c, kind: "request_info"}); setNote(""); }}
                      className="rounded-full uppercase tracking-[0.22em] text-xs h-9 px-4 border-amber-400/50 text-amber-300 hover:bg-amber-400/10"
                      data-testid={`admin-info-${c.id}`}>
                      <MessageCircleWarning size={13} className="mr-1.5" /> Request info
                    </Button>
                    <Button variant="outline"
                      onClick={() => { setAction({claim: c, kind: "reject"}); setNote(""); }}
                      className="rounded-full uppercase tracking-[0.22em] text-xs h-9 px-4 border-red-400/50 text-red-300 hover:bg-red-400/10"
                      data-testid={`admin-reject-${c.id}`}>
                      <XCircle size={13} className="mr-1.5" /> Reject
                    </Button>
                  </div>
                )}
                {c.admin_note && (
                  <div className="mt-3 text-xs text-white/60 border-l-2 border-white/15 pl-3">
                    <div className="text-[9px] uppercase tracking-[0.22em] text-white/40 mb-1">Last note</div>
                    {c.admin_note}
                  </div>
                )}
                {c.rejection_reason && (
                  <div className="mt-3 text-xs text-red-200 border-l-2 border-red-400/40 pl-3">
                    <div className="text-[9px] uppercase tracking-[0.22em] text-red-300/70 mb-1">Rejection reason</div>
                    {c.rejection_reason}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview dialog */}
      <Dialog open={!!previewSrc} onOpenChange={(v) => { if (!v) { setPreviewSrc(null); setPreviewMime(null); } }}>
        <DialogContent className="bg-[#0c0c0e] border-white/10 sm:max-w-4xl h-[80vh]" data-testid="cert-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">Certificate preview</DialogTitle>
          </DialogHeader>
          {previewSrc && previewMime?.startsWith("image/") ? (
            <img src={previewSrc} alt="cert" className="max-w-full max-h-[60vh] mx-auto object-contain rounded" />
          ) : previewSrc ? (
            <iframe src={previewSrc} className="w-full h-[60vh] rounded bg-white" title="cert" />
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Action dialog */}
      <Dialog open={!!action} onOpenChange={(v) => { if (!v) { setAction(null); setNote(""); } }}>
        <DialogContent className="bg-[#0c0c0e] border-white/10 sm:max-w-lg" data-testid="admin-action-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">
              {action?.kind === "approve" && `Approve ${action.claim.association_name}`}
              {action?.kind === "request_info" && `Request info on ${action.claim.association_name}`}
              {action?.kind === "reject" && `Reject ${action?.claim.association_name}`}
            </DialogTitle>
            <DialogDescription className="text-white/55">
              {action?.kind === "approve" && "Optional note. The claimant will be notified by email."}
              {action?.kind === "request_info" && "Tell the claimant exactly what's missing so they can reply directly to onboarding@callsheet.app."}
              {action?.kind === "reject" && "Reason will be shown to the claimant and appear on /my-claims."}
            </DialogDescription>
          </DialogHeader>
          <Textarea value={note} onChange={e => setNote(e.target.value)}
            placeholder={action?.kind === "approve"
              ? "Optional — 'Verified via registration certificate + secretariat call.'"
              : action?.kind === "request_info"
              ? "e.g. Please also share your 2024 renewal receipt PDF."
              : "e.g. Registration certificate expired in 2019."}
            className="bg-black/40 border-white/15 min-h-[120px]"
            data-testid="admin-action-note" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setAction(null); setNote(""); }}>Cancel</Button>
            <Button onClick={submitAction} disabled={saving}
              className="btn-themed rounded-full uppercase tracking-[0.22em] text-xs px-5"
              data-testid="admin-action-submit">
              {saving ? (<><Loader2 size={12} className="animate-spin mr-2" /> Saving…</>) : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
